import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import type { Job, InvoiceTemplate } from "../../types";

const DEFAULT_ACCENT = "#1d4ed8";

function parseAccent(hex: string | undefined): string {
  if (!hex || typeof hex !== "string") return DEFAULT_ACCENT;
  const s = hex.trim().replace(/^#/, "");
  if (/^[0-9A-Fa-f]{6}$/.test(s)) return `#${s}`;
  if (/^[0-9A-Fa-f]{3}$/.test(s)) return `#${s[0]}${s[0]}${s[1]}${s[1]}${s[2]}${s[2]}`;
  return DEFAULT_ACCENT;
}

const FONT_SCALE = { small: 0.9, normal: 1, large: 1.15 } as const;

function buildStyles(template?: InvoiceTemplate | null) {
  const theme = template?.theme ?? "classic";
  const accent = parseAccent(template?.accentColor);
  const fontScale = FONT_SCALE[template?.fontSize ?? "normal"];
  const baseFontSize = Math.round(11 * fontScale);
  const titleFontSize = Math.round((template?.theme === "luxe" ? 20 : 18) * fontScale);
  const companyFontSize = Math.round((theme === "luxe" ? 18 : 16) * fontScale);
  const totalFontSize = Math.round(16 * fontScale);
  const labelFontSize = Math.round(9 * fontScale);

  const isMinimal = theme === "minimal";
  const isLuxe = theme === "luxe";

  const titleBarStyle = isLuxe
    ? {
        width: 48,
        height: 3,
        backgroundColor: accent,
        marginTop: 6,
        marginBottom: 4,
      }
    : null;

  const base = {
    page: {
      padding: 40,
      fontSize: baseFontSize,
    },
    header: {
      marginBottom: 24,
      flexDirection: "row" as const,
      alignItems: "flex-start" as const,
      justifyContent: "space-between" as const,
      borderBottomWidth: isMinimal ? 0.5 : 1,
      borderBottomColor: isMinimal ? "#e5e7eb" : isLuxe ? "#1f2937" : "#e5e7eb",
      paddingBottom: 16,
    },
    headerLeft: {
      flex: 1,
    },
    logo: {
      width: 56,
      height: 56,
      marginBottom: 4,
    },
    companyName: {
      fontSize: companyFontSize,
      fontWeight: "bold" as const,
      marginBottom: 4,
      color: "#111827",
    },
    companyLine: {
      marginBottom: 2,
      color: "#4b5563",
      fontSize: Math.round(10 * fontScale),
    },
    title: {
      fontSize: titleFontSize,
      fontWeight: "bold" as const,
      marginTop: 20,
      marginBottom: 16,
      color: accent,
      letterSpacing: 0.5,
    },
    section: {
      marginBottom: 12,
    },
    label: {
      fontSize: labelFontSize,
      color: "#6b7280",
      marginBottom: 2,
      textTransform: "uppercase" as const,
      letterSpacing: 0.5,
    },
    value: {
      fontSize: baseFontSize,
      color: "#374151",
    },
    introText: {
      fontSize: baseFontSize,
      color: "#4b5563",
      marginBottom: 16,
      lineHeight: 1.4,
    },
    termsText: {
      fontSize: Math.round(9 * fontScale),
      color: "#6b7280",
      marginTop: 16,
      marginBottom: 12,
      lineHeight: 1.35,
    },
    row: {
      flexDirection: "row" as const,
      justifyContent: "space-between" as const,
      marginTop: 24,
      paddingTop: 16,
      borderTopWidth: isMinimal ? 0.5 : 1.5,
      borderTopColor: isLuxe ? accent : "#e5e7eb",
      alignItems: "center" as const,
    },
    totalLabel: {
      fontSize: Math.round(12 * fontScale),
      fontWeight: "bold" as const,
      color: "#111827",
    },
    totalValue: {
      fontSize: totalFontSize,
      fontWeight: "bold" as const,
      color: accent,
    },
    footer: {
      position: "absolute" as const,
      bottom: 30,
      left: 40,
      right: 40,
      fontSize: Math.round(8 * fontScale),
      color: "#9ca3af",
      textAlign: "center" as const,
    },
  };
  return { styles: StyleSheet.create(base), titleBarStyle };
}

export interface DevisFactureDocProps {
  type: "devis" | "facture";
  job: Job;
  companyName?: string;
  companySiret?: string;
  companyAddress?: string;
  companyPhone?: string;
  amountEur: number;
  date: string;
  /** Template personnalisé (thème, couleur, pied de page, logo) */
  template?: InvoiceTemplate | null;
}

export function DevisFactureDoc({
  type,
  job,
  companyName,
  companySiret,
  companyAddress,
  companyPhone,
  amountEur,
  date,
  template,
}: DevisFactureDocProps) {
  const title = type === "devis" ? "DEVIS" : "FACTURE";
  const dossierLabel =
    job.dossierType === "deshumidificateur"
      ? "déshumidificateur"
      : job.dossierType === "vmc_agricoles"
        ? "VMC Agricoles"
      : job.dossierType === "th_122"
        ? "TH 122"
      : job.dossierType === "renovation"
        ? "Rénovation"
      : job.dossierType === "hp_bp_flottante"
        ? "HP/BP flottante"
      : job.dossierType === "pac"
        ? "PAC"
        : job.dossierType === "led_interieur"
          ? "LED intérieur"
          : job.dossierType === "led_exterieur"
            ? "LED extérieur"
            : "LED";
  const { styles, titleBarStyle } = buildStyles(template);
  const defaultFooter =
    "Document généré par Régie Connect · " +
    (type === "devis" ? "Devis" : "Facture") +
    " à titre informatif. Conditions de paiement et mentions légales à définir selon votre activité.";
  const footerText = template?.footerText?.trim() || defaultFooter;

  const orientation = template?.pageOrientation === "landscape" ? "landscape" : "portrait";
  const logoOnRight = template?.logoPosition === "right";
  const introText = template?.introText?.trim();
  const termsText = template?.termsText?.trim();

  return (
    <Document>
      <Page size="A4" orientation={orientation} style={styles.page}>
        <View style={[styles.header, logoOnRight ? { flexDirection: "row-reverse" as const } : {}]}>
          <View style={[styles.headerLeft, logoOnRight ? { alignItems: "flex-end" as const } : {}]}>
            {template?.logoUrl ? (
              <Image src={template.logoUrl} style={styles.logo} />
            ) : null}
            {companyName ? (
              <Text style={styles.companyName}>{companyName}</Text>
            ) : (
              <Text style={styles.companyName}>Régie Connect</Text>
            )}
            {companyAddress ? (
              <Text style={styles.companyLine}>{companyAddress}</Text>
            ) : null}
            {companyPhone ? (
              <Text style={styles.companyLine}>Tél. {companyPhone}</Text>
            ) : null}
            {companySiret ? (
              <Text style={styles.companyLine}>SIRET : {companySiret}</Text>
            ) : null}
          </View>
        </View>

        <View>
          <Text style={styles.title}>{title}</Text>
          {titleBarStyle ? <View style={titleBarStyle} /> : null}
        </View>

        {introText ? (
          <View style={styles.section}>
            <Text style={styles.introText}>{introText}</Text>
          </View>
        ) : null}

        <View style={styles.section}>
          <Text style={styles.label}>Client</Text>
          <Text style={styles.value}>{job.clientName}</Text>
          <Text style={styles.value}>{job.clientAddress}</Text>
          {job.clientPhone ? (
            <Text style={styles.value}>Tél. {job.clientPhone}</Text>
          ) : null}
        </View>

        <View style={styles.section}>
          <Text style={styles.label}>Date</Text>
          <Text style={styles.value}>{date}</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.label}>Désignation</Text>
          <Text style={styles.value}>
            Prestation installation {dossierLabel}
            {job.surface ? ` · Surface : ${job.surface} m²` : ""}
            {job.lightingType ? ` · ${job.lightingType}` : ""}
          </Text>
        </View>

        <View style={styles.row}>
          <Text style={styles.totalLabel}>
            Total {type === "devis" ? "TTC" : "TTC"}
          </Text>
          <Text style={styles.totalValue}>
            {amountEur.toLocaleString("fr-FR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}{" "}
            €
          </Text>
        </View>

        {termsText ? (
          <View style={styles.section}>
            <Text style={styles.termsText}>{termsText}</Text>
          </View>
        ) : null}

        <View style={styles.footer}>
          <Text>{footerText}</Text>
        </View>
      </Page>
    </Document>
  );
}
